import React from "react";
import { CollapseFAQ, H3, NamedLink } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';

import ContinueIcon from '../../TopbarContainer/Topbar/images/continue-icon.svg';
import CanadaMountains from '../images/CANADA - Nature - john-lee-oMneOBYhJxY-unsplash.jpg'
import Montreal from '../images/MONTREAL skyline - michael-descharles-NMkECjnU0IY-unsplash.jpg';

import css from '../HouseSittingAbroadPage.module.css';
import { canadaFaq } from "./faqs";

const CanadaPage = props => {
    return (
        <div className={css.root}>
            <H3>
                House Sitting in Canada
            </H3>
            <p className={css.info}>
                House sitting in Canada involves a temporary living arrangement where a house sitter takes care of a homeowner's property and often pets while the homeowner is away. This arrangement allows house sitters to live like locals, experiencing Canadian culture and outdoor activities while providing peace of mind to homeowners.
            </p>
            <br />
            <p className={css.info}>
                Whether you're looking for a short-term stay or a longer-term arrangement, house sitting in Canada offers a unique opportunity to explore this vast and diverse country.
            </p>

            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>

            <div className={css.imageContainerHorizontal}>
                <img className={css.image} src={CanadaMountains} alt='Photo by John Lee' />
                <p className={css.info}>
                    Photo by John Lee
                </p>
            </div>

            <p className={css.subtitle}>
                Why Choose HouseSit Match?
            </p>
            <p className={css.info}>
                HouseSit Match is a leading platform for house and pet sitting in Canada, offering a safe and efficient way to connect homeowners with reliable sitters. With our extensive network, detailed profiles, and secure messaging system, HouseSit Match makes finding and applying for house sitting opportunities easy and stress-free.
            </p>
            <br />
            <p className={css.info}>
                Our commitment to quality and trust ensures that both homeowners and house sitters have a positive experience throughout the process.
            </p>

            <p className={css.subtitle} style={{ marginBottom: 0 }}>
                The Benefits of House Sitting in Canada
            </p>

            <ul className={css.list} style={{ marginTop: 0 }}>
                <li>
                    <strong>Explore Canada as a Local: </strong>
                    House sitters have the opportunity to live like locals, experiencing the culture, food, and natural beauty of Canada in a unique way.
                </li>
                <li>
                    <strong>Affordable Accommodation: </strong>
                    House sitting in Canada allows you to stay for free in comfortable homes across the country, saving on accommodation costs.
                </li>
                <li>
                    <strong>Pet Care Experience: </strong>
                    Sitter duties often include caring for pets, providing a rewarding experience for animal lovers.
                </li>
                <li>
                    <strong>Flexible Stay: </strong>
                    Depending on the agreement, stays can range from a few days to several months, giving you flexibility in your travel plans.
                </li>
                <li>
                    <strong>Access to Top Canadian Cities: </strong>
                    Many house sitting opportunities are available in popular Canadian cities, making it a great way to explore without the high cost of renting.
                </li>
            </ul>

            <p className={css.subtitle} style={{ marginBottom: 0 }}>
                Types of House Sitting Arrangements in Canada
            </p>

            <ul className={css.list} style={{ marginTop: 0 }}>
                <li>
                    <strong>Short-Term House Sitting: </strong>
                    Ideal for travelers looking to explore Canada for a few days to a couple of weeks.
                </li>
                <li>
                    <strong>Long-Term House Sitting: </strong>
                    Suitable for those planning an extended stay in Canada, offering a more immersive experience.
                </li>
                <li>
                    <strong>Pet Sitting Assignments: </strong>
                    Care for pets in their own home, a great option for animal lovers.
                </li>
                <li>
                    <strong>Snowbird Sit: </strong>
                    Specifically for homeowners heading to warmer climates, leaving their properties in the care of responsible sitters during the winter months.
                </li>
            </ul>

            <p className={css.subtitle} style={{ marginBottom: 0 }}>
                Popular Locations for House Sitting in Canada
            </p>

            <ul className={css.listNumbers} style={{ marginTop: 0 }}>
                <li>
                    <strong>Vancouver, British Columbia: </strong>
                    Enjoy the urban vibrancy, access to outdoor activities like skiing, hiking, and the stunning natural scenery.
                </li>
                <li>
                    <strong>Toronto, Ontario: </strong>
                    A bustling city with a diverse culture, food, and entertainment options.
                </li>
                <li>
                    <strong>Calgary, Alberta: </strong>
                    Known for its beautiful surroundings and the famous Calgary Stampede, offering both city and outdoor adventures.
                </li>
                <li>
                    <strong>Montreal, Quebec: </strong>
                    A European feel with rich history, vibrant arts scene, and fantastic food.
                </li>
                <li>
                    <strong>Victoria, British Columbia: </strong>
                    Peaceful and picturesque, known for its British heritage and access to beautiful landscapes.
                </li>
            </ul>

            <div className={css.imageContainerHorizontal}>
                <img className={css.image} src={Montreal} alt="some housesitters travel around the world by pet sitting" />
                <p className={css.info}>
                    Photo by Michael Descharles
                </p>
            </div>

            <p className={css.subtitle}>
                Costs of House Sitting in Canada
            </p>
            <p className={css.info}>
                House sitting in Canada typically involves no costs for the sitter beyond the cost of travel and a small membership fee to join house sitting platforms like HouseSit Match. Homeowners often provide free accommodation and sometimes a stipend for daily expenses. This makes house sitting a highly affordable option for travelers looking to experience Canada without spending on hotels or other forms of accommodation.
            </p>

            <p className={css.subtitle}>
                Visa Regulations in Canada
            </p>

            <p className={css.info}>
                For house sitters coming from abroad, it's important to understand Canadian visa requirements. Most travelers need a visa or an Electronic Travel Authorization (eTA) to visit Canada. The requirements vary depending on the traveler's nationality and the purpose of their visit.
            </p>
            <br />
            <p className={css.info}>
                It's advisable to consult the Immigration, Refugees, and Citizenship Canada (IRCC) website or consult with a legal expert to ensure compliance with all visa regulations before applying for a house sitting opportunity.
            </p>

            <p className={css.subtitle} style={{ marginBottom: 0 }}>
                How to Become a House Sitter in Canada
            </p>

            <ul className={css.listNumbers} style={{ marginTop: 0 }}>
                <li>
                    <strong>Join a House Sitting Platform: </strong>
                    Sign up on platforms like HouseSit Match to create a profile.
                </li>
                <li>
                    <strong>Complete Your Profile: </strong>
                    Add detailed information about your experience with pets and home care, as well as your travel plans.
                </li>
                <li>
                    <strong>Search and Apply for Assignments: </strong>
                    Browse through available house sitting jobs in Canada and apply for those that match your preferences and schedule.
                </li>
                <li>
                    <strong>Prepare for Interviews: </strong>
                    Be ready to answer questions about your experience and why you'd be a good fit for the homeowner's needs.
                </li>
                <li>
                    <strong>Prepare for the Stay: </strong>
                    Research the local area, understand the responsibilities involved, and pack accordingly for the Canadian climate.
                </li>
            </ul>

            <H3>
                FAQs
            </H3>

            <CollapseFAQ
                list={canadaFaq}
            />

            <p className={css.subtitle}>
                Testimonials
            </p>
            <p className={css.info}>
                Include real testimonials from sitters who have successfully completed house sitting assignments in Canada.
            </p>

            <p className={css.subtitle}>
                Embrace the House Sitting Lifestyle in Canada
            </p>
            <p className={css.info}>
                House sitting in Canada offers a unique blend of cultural immersion, affordable travel, and rewarding pet care opportunities.
            </p>
            <br />
            <p className={css.info}>
                By choosing HouseSit Match, you can find trusted house sitting assignments that allow you to experience Canada like a local. Whether you're looking for a short stay in Vancouver or a longer-term arrangement in Montreal, house sitting provides the perfect blend of freedom and community.
            </p>
            <br />
            <p className={css.info}>
                Start your house sitting journey today by{" "}
                <NamedLink name="SignupPage">registering with our platform</NamedLink>
                {" "}and explore Canada from a new perspective!
            </p>

            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>
        </div>
    )
};

export default CanadaPage;